import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";

const KEY = "homio_cookie_consent";

export default function CookieBanner({ settings }) {
  const [show, setShow] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false, preferences: false });
  const c = settings?.cookie || {};

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (!saved) setShow(true);
    else {
      try { setPrefs(JSON.parse(saved)); } catch {}
    }
    const opener = () => { setManage(true); setShow(true); };
    window.addEventListener("open-cookie-settings", opener);
    return () => window.removeEventListener("open-cookie-settings", opener);
  }, []);

  const save = (values) => {
    localStorage.setItem(KEY, JSON.stringify(values));
    setPrefs(values);
    setShow(false);
    setManage(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-border p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#C81D35]/10 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#C81D35]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-[#111827]">Your privacy</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {c.banner_text || "We use cookies to improve your experience, analyse traffic and personalise content. You can accept all, reject non-essential, or manage preferences."}
            </p>

            {manage && (
              <div className="mt-4 space-y-3" data-testid="cookie-preferences">
                <PrefRow label="Necessary" desc="Required for the site to function." checked disabled />
                <PrefRow label="Analytics" desc="Help us understand how the site is used." checked={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} />
                <PrefRow label="Marketing" desc="Used to deliver relevant content." checked={prefs.marketing} onChange={(v) => setPrefs({ ...prefs, marketing: v })} />
                <PrefRow label="Preferences" desc="Remember your choices." checked={prefs.preferences} onChange={(v) => setPrefs({ ...prefs, preferences: v })} />
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => save({ necessary: true, analytics: true, marketing: true, preferences: true })} className="bg-[#C81D35] hover:bg-[#a5162b] rounded-full" data-testid="cookie-accept-all">
                {c.accept_text || "Accept all"}
              </Button>
              <Button variant="outline" onClick={() => save({ necessary: true, analytics: false, marketing: false, preferences: false })} className="rounded-full" data-testid="cookie-reject">
                {c.reject_text || "Reject non-essential"}
              </Button>
              {manage ? (
                <Button variant="ghost" onClick={() => save(prefs)} className="rounded-full" data-testid="cookie-save-prefs">Save preferences</Button>
              ) : (
                <Button variant="ghost" onClick={() => setManage(true)} className="rounded-full" data-testid="cookie-manage">
                  {c.manage_text || "Manage preferences"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-border/60 last:border-0">
      <div>
        <p className="text-sm font-semibold text-[#111827]">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
